import { useRouter } from "@tanstack/react-router"
import { runInAction } from "mobx"
import { observer } from "mobx-react-lite"
import { useEffect, useMemo, useRef } from "react"

import { Centered } from "@/components/Centered/Centered.ts"
import { Chat } from "@/components/Chat/Chat.tsx"
import { principalCache } from "@/components/Chat/ChatModel.ts"
import { TypingIndicator } from "@/components/Chat/TypingIndicator.tsx"
import { GradientBlob } from "@/components/GradientBlob.ts"
import { PromptInput } from "@/components/PromptInput/PromptInput.tsx"
import { ResizableSplitter } from "@/components/ResizableSplitter/ResizableSplitter.tsx"
import { Scrollable } from "@/components/Scrollable/Scrollable.tsx"
import { ScrollableModelImpl } from "@/components/Scrollable/ScrollableModel.ts"
import { Spinner } from "@/components/Spinner/Spinner.tsx"
import { useDevMode } from "@/hooks/useDevMode.ts"
import { useElementHeight } from "@/hooks/useElementHeight.ts"
import { useMediaQuery } from "@/hooks/useMediaQuery.ts"
import { useModel } from "@/hooks/useModel.ts"
import { useMyProfile } from "@/hooks/useProfile.ts"
import { useOrgContext } from "@/hooks/useRootContext.ts"
import { pageTitle, projectDisplayName } from "@/lib/displayUtils.ts"
import { mapNotNull } from "@/lib/std.ts"
import { Page } from "@/routes/auth/Page.tsx"
import { useCurrentProject } from "@/routes/projects/projectHooks.ts"
import { useCurrentTask, useTaskRoute } from "@/routes/tasks/tasksHooks.ts"
import { TaskViewId } from "@/routes/tasks/TaskViewId.ts"
import { Breadcrumbs } from "@/routes/tasks/TaskViewScene/Breadcrumbs.tsx"
import { TaskResultTabs } from "@/routes/tasks/TaskViewScene/TaskResultTabs.tsx"
import { TaskVersionsTab } from "@/routes/tasks/TaskViewScene/TaskVersionsTab.tsx"
import { TaskViewModel, TaskViewModelContext } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"
import { TaskViewSceneStyles } from "@/routes/tasks/TaskViewScene/TaskViewSceneStyles.ts"
import { mobileBreakpoint } from "@/styles/mixins.ts"
import { ellipsize } from "@/styles/textStyles.ts"

import type { ChatMessageModel } from "@/components/Chat/ChatModel.ts"
import type { AiChatMessageAuthor, MentionMetadata } from "@/lib/client/generated/TypeDefinitions.ts"


export const TaskViewScene = observer(() => {
  const context = useOrgContext()

  const router = useRouter()

  const route = useTaskRoute()

  const myProfile = useMyProfile()
  const devMode = useDevMode()

  const params = route.useParams()
  const search = route.useSearch()

  const project = useCurrentProject()
  const task = useCurrentTask()

  const model = useModel(() => new TaskViewModel({
    context: context,
    queries: context.queries,
    router: router,

    orgId: context.orgId,
    project: project,
    taskId: params.taskId,
    tab: search.tab,
    view: search.view,
    message: search.message,
    taskWithDetails: task,
    devMode: devMode
  }), [params.taskId])

  const inputContainerRef = useRef<HTMLDivElement>(null)
  const scrollableModel = useModel(() => new ScrollableModelImpl())

  useEffect(() => model.setCurrentTab(search.tab), [model, search.tab])

  useEffect(() => model.setCurrentView(search.view), [model, search.view])

  useEffect(() => {
    if (search.message) {
      model.chatModel.navigate(search.message)
    }
  }, [model.chatModel, search.message])

  const isMobile = useMediaQuery(mobileBreakpoint)

  const taskViewId = model.currentView.get()
  const chatOnly = isMobile

  const messages = model.chatModel.messages.get()
  const talkingToOtherUsers = useMemo(
    () => myProfile ? getTalkingToOtherUsers(messages, myProfile.username) : null,
    [messages, myProfile]
  )

  const initProjectStep = model.initProjectStep.get()

  const chatWidth = `${model.chatPercentage.get()}%`
  const tabsWidth = `${model.tabsPercentage.get()}%`

  const stopAction = (model.agentIsRunning.get() || initProjectStep == "CreatingRepository" || initProjectStep == "WaitForMirroring") ? () => {
    void context.queries.apiClient.cancelTaskProcess(model.taskId)
    runInAction(() => model.chatModel.waitingForResponse.set(0))
  } : undefined

  const resizable = !chatOnly && taskViewId != TaskViewId.Chat
  const stickyChatInput = resizable || initProjectStep == null

  const inputContainerHeight = useElementHeight(inputContainerRef, 120, [stickyChatInput])

  return (
    <TaskViewModelContext value={model}>
      <Page>
        <title>{pageTitle(model.task.get()?.title, projectDisplayName(project.name))}</title>
        <TaskViewSceneStyles.Container chatOnly={chatOnly}>
          <TaskViewSceneStyles.ChatContainer
            chatOnly={chatOnly}
            width={chatOnly ? undefined : chatWidth}
            data-resizable-container={chatWidth}
          >
            <TaskViewSceneStyles.ChatContainerInner chatOnly={chatOnly}>
              {initProjectStep === "EnterUrl" || initProjectStep === "EnterProjectName" && (
                <TaskViewSceneStyles.NewProjectHeader>
                  Configuring the new project
                </TaskViewSceneStyles.NewProjectHeader>
              )}

              <TaskViewSceneStyles.Title chatOnly={chatOnly}>
                <Breadcrumbs model={model} />
              </TaskViewSceneStyles.Title>

              <TaskViewSceneStyles.ScrollableWithInput resizable={resizable}>
                <Scrollable model={scrollableModel}>
                  {model.isLoading.get()
                    ? <Centered><Spinner text={ellipsize("Loading chat")} /></Centered>
                    : (
                      <Chat
                        model={model}
                        isAgentRunning={model.agentIsRunning}
                        inputContainerRef={inputContainerRef}
                        resizable={resizable}
                      />
                    )
                  }

                  {!stickyChatInput && model.showChatInput.get() && (
                    <TaskViewSceneStyles.InputWrapper
                      ref={inputContainerRef}
                      marginTop={`${-inputContainerHeight}px`}
                      resizable={resizable}
                      transparent
                    >
                      <PromptInput
                        model={model.inputModel}
                        task={model.task.get()}
                        stopAction={stopAction}
                        talkingToOtherUsers={talkingToOtherUsers}
                        contextLoadModel={model}
                      />
                    </TaskViewSceneStyles.InputWrapper>
                  )}
                </Scrollable>
                {stickyChatInput && model.showChatInput.get() && (
                  <TaskViewSceneStyles.InputContainer
                    ref={inputContainerRef}
                    scrollbarWidth={scrollableModel.scrollbarWidth.get()}
                  >
                    <TaskViewSceneStyles.TypingIndicatorContainer
                      scrollbarWidth={scrollableModel.scrollbarWidth.get()}
                    >
                      <TypingIndicator model={model.chatModel.typingIndicatorModel} />
                    </TaskViewSceneStyles.TypingIndicatorContainer>
                    <TaskViewSceneStyles.InputWrapper resizable={resizable}>
                      <PromptInput
                        model={model.inputModel}
                        task={model.task.get()}
                        stopAction={stopAction}
                        talkingToOtherUsers={talkingToOtherUsers}
                        contextLoadModel={model}
                      />
                    </TaskViewSceneStyles.InputWrapper>
                  </TaskViewSceneStyles.InputContainer>
                )}
              </TaskViewSceneStyles.ScrollableWithInput>
            </TaskViewSceneStyles.ChatContainerInner>
          </TaskViewSceneStyles.ChatContainer>

          {resizable && (
            <ResizableSplitter
              onResize={model.updateSplitPercentages}
              initialLeftPercentage={model.chatPercentage.get()}
              minLeftPercentage={20}
              maxLeftPercentage={80}
              minLeftPixels={400}
              minRightPixels={520}
            />
          )}

          {!chatOnly && taskViewId == TaskViewId.Full && (
            <TaskViewSceneStyles.TabsContainer
              data-testid="task-view-part"
              data-resizable-container={tabsWidth}
              width={tabsWidth}
            >
              <TaskResultTabs model={model} />
            </TaskViewSceneStyles.TabsContainer>
          )}

          {!chatOnly && taskViewId == TaskViewId.Versions && (
            <TaskViewSceneStyles.TabsContainer data-resizable-container={tabsWidth} width={tabsWidth}>
              <Scrollable>
                <TaskVersionsTab model={model} />
              </Scrollable>
            </TaskViewSceneStyles.TabsContainer>
          )}

          {!stickyChatInput && <GradientBlob />}
        </TaskViewSceneStyles.Container>
      </Page>
    </TaskViewModelContext>
  )
})

const getTalkingToOtherUsers = (messages: ChatMessageModel[], myUsername: string) => {
  let found: string[] | null = null

  messages.slice().reverse().find((message) => {
    if (message.content.className != "Text" || message.content.text.metadata.mentions.length == 0) {
      return false
    }

    const messageAuthorPrincipal = message.author as AiChatMessageAuthor.Principal
    const messageAuthorName = principalCache.get(messageAuthorPrincipal.pid)?.name ?? null

    const text = message.content.text
    const mentions = text.metadata.mentions
      .filter((m: MentionMetadata) => m.type == "USER")
      .map((m: MentionMetadata) => text.value.substring(text.value.startsWith("@") ? m.fromIndex + 1 : m.fromIndex, m.toIndex + 1))
      .map((username: string) =>
        (username == myUsername) ? messageAuthorName : username
      )

    if (mentions.length > 0) {
      found = mapNotNull(mentions, (name) => name)
    }

    return mentions.length > 0
  })

  return found
}
